import * as React from 'react';
import { customArcherColors, theme } from '../../utils';
import { IconButton } from '../IconButton';
import { Typography } from '../Typography';
import { Snackbar as MatSnackbar, snackbarContentClasses, SxProps, Theme } from '@mui/material';

const styles: Record<string, SxProps<Theme>> = {
  success: {
    '& div': {
      backgroundColor: customArcherColors.alertColors && customArcherColors.alertColors.positive
    }
  },
  error: {
    '& div': {
      backgroundColor: customArcherColors.alertColors && customArcherColors.alertColors.error
    }
  },
  info: {
    '& div': {
      backgroundColor: customArcherColors.alertColors && customArcherColors.alertColors.informative
    }
  },
  warning: {
    '& div': {
      backgroundColor: customArcherColors.alertColors && customArcherColors.alertColors.warning
    }
  },
  snackbarRoot: {
    margin: 0,
    top: 0,
    padding: theme.spacing(0),
    '&>div': {
      margin: theme.spacing(0),
      padding: theme.spacing(2),
      flexWrap: 'nowrap',
      minWidth: 'auto'
    },
    [`& .${snackbarContentClasses['action']}`]: {
      margin: 0,
      padding: 0,
      '&>button': {
        color: theme.palette.background.paper,
        margin: theme.spacing(-1),
        padding: theme.spacing(1)
      },
      '&>button:hover': {
        backgroundColor: 'rgba(255,255,255, .12)',
        borderRadius: 50
      },
      '&>button:focus': {
        backgroundColor: 'rgba(255,255,255, .25)',
        borderRadius: 50
      }
    },
    [`& .${snackbarContentClasses['message']}`]: {
      margin: theme.spacing(0, 2, 0, 0),
      padding: 0,
      maxWidth: '500px',
      '&>h4': {
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        overflow: 'hidden'
      }
    }
  },
  noCloseAction: {
    [`& .${snackbarContentClasses['message']}`]: {
      margin: theme.spacing(0)
    }
  }
};

export type SnackBarVariants = 'success' | 'error' | 'info' | 'warning';

export interface SnackBarProps {
  open: boolean;
  onRequestClose: () => void;
  message: string;
  translate: any;
  closeAction?: boolean;
  variant?: SnackBarVariants;
  sx?: SxProps<Theme>;
}

const SnackBar = ({ open, message, onRequestClose, closeAction, variant, translate, sx }: SnackBarProps) => {
  const { success, error, info, warning, snackbarRoot, noCloseAction } = styles;
  const getClass = () => {
    let result = success;
    if (variant) {
      switch (variant) {
        case 'error':
          result = error;
          break;
        case 'info':
          result = info;
          break;
        case 'warning':
          result = warning;
          break;
        default:
          result = success;
          break;
      }
    }
    return result;
  };

  return (
    <MatSnackbar
      open={open}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      autoHideDuration={8000}
      role="alertdialog"
      onClose={onRequestClose}
      message={
        <Typography variant="h4" component="p">
          {message}
        </Typography>
      }
      action={
        closeAction && (
          <IconButton
            icon="Close"
            fontSize="small"
            color="inherit"
            key="snackbar-close"
            onClick={onRequestClose}
            aria-label={translate('cmmn:clear')}
          />
        )
      }
      sx={[getClass(), snackbarRoot, closeAction ? undefined : noCloseAction, sx] as SxProps<Theme>}
    />
  );
};

SnackBar.displayName = 'Snackbar';

export { SnackBar };
